import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';

const FIRESTORE_IN_LIMIT = 10;

function chunkArray(items, size) {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

function isPublicGearReview(review) {
  return review.type === 'renter-review' || !review.type;
}

export async function fetchReviewSummariesForGear(gearIds = []) {
  const uniqueIds = [...new Set(gearIds.filter(Boolean))];
  const summaries = {};

  if (!uniqueIds.length) return summaries;

  const chunks = chunkArray(uniqueIds, FIRESTORE_IN_LIMIT);

  await Promise.all(
    chunks.map(async (ids) => {
      const reviewsQuery = query(collection(db, 'reviews'), where('gearId', 'in', ids));
      const snapshot = await getDocs(reviewsQuery);
      snapshot.docs.forEach((docSnap) => {
        const review = docSnap.data();
        if (!isPublicGearReview(review) || !review.gearId) return;

        const existing = summaries[review.gearId] || { total: 0, count: 0 };
        existing.total += Number(review.rating) || 0;
        existing.count += 1;
        summaries[review.gearId] = existing;
      });
    })
  );

  Object.keys(summaries).forEach((gearId) => {
    const entry = summaries[gearId];
    entry.rating = entry.count > 0 ? entry.total / entry.count : 0;
  });

  return summaries;
}

export function attachReviewSummaries(gearList = [], summaries = {}) {
  return gearList.map((gear) => {
    const summary = summaries[gear.id];
    if (!summary || summary.count < 1) return gear;
    return {
      ...gear,
      reviewSummary: {
        rating: summary.rating,
        count: summary.count,
      },
    };
  });
}

/** Two-letter US state / territory codes — always displayed uppercase. */
const US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
]);

const SMALL_WORDS = new Set(['and', 'or', 'of', 'the', 'in', 'on', 'at', 'to', 'for', 'by']);

function titleCaseToken(token, index) {
  if (!token) return token;
  if (/^\d{5}(-\d{4})?$/.test(token)) return token;
  if (/^[a-zA-Z]{2}$/.test(token) && US_STATE_CODES.has(token.toUpperCase())) {
    return token.toUpperCase();
  }
  const lower = token.toLowerCase();
  if (index > 0 && SMALL_WORDS.has(lower)) return lower;
  // Hyphenated (Winston-Salem) and apostrophe (O'Brien) names
  return lower.replace(/(^|[-'])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());
}

/**
 * Normalize a location string for display/storage:
 * "colorado springs, co" → "Colorado Springs, CO"
 * "denver, colorado" → "Denver, Colorado"
 */
export function formatLocationDisplay(locationText) {
  if (locationText == null) return '';
  const raw = String(locationText).trim().replace(/\s+/g, ' ');
  if (!raw) return '';

  return raw
    .split(',')
    .map((segment) =>
      segment
        .trim()
        .split(/\s+/)
        .map((token, index) => titleCaseToken(token, index))
        .join(' ')
    )
    .filter(Boolean)
    .join(', ');
}

export function formatGearLocationLabel(location) {
  if (!location) return 'Location on listing';
  if (typeof location === 'string') {
    return formatLocationDisplay(location) || 'Location on listing';
  }
  if (typeof location === 'object') {
    const city = formatLocationDisplay(location.city || '');
    const state = formatLocationDisplay(location.state || '');
    const zip = location.zip ? String(location.zip).trim() : '';
    const combined = [city, state].filter(Boolean).join(', ');
    if (combined && zip) return `${combined} ${zip}`;
    if (combined) return combined;
    if (zip) return zip;
    return 'Location on listing';
  }
  return 'Location on listing';
}